import { defineStore } from "pinia";
import APIMenuMapper, {
  type CatalogItem,
  type CatalogItemJoined,
  type ItemData,
  type SubItemData,
} from "~/utils/apiMenuMapper";
import { readMirror, writeMirror } from "~/services/repos/baseRepo";
import { registerResource } from "~/services/repos/versionSync";

export type StockCacheKey =
  | "stockList"
  | "productMenu"
  | "toppingMenu"
  | "stockFormula"
  | "toppingFormula"
  | "stockTransactions"
  | "stockUserPins";

export interface CachedFetchOptions {
  force?: boolean;
}

export interface CachedEntry<T> {
  data: T | null;
  lastFetchedAt: number;
  loading: boolean;
  error: unknown;
  inFlightPromise: Promise<T> | null;
  storeId: string;
}

interface ProductMenuData {
  catalogs: CatalogItem[];
  items: ItemData[];
  subItems: SubItemData[];
  productCatalogs: CatalogItemJoined[];
}

interface ToppingMenuData {
  groups: any[];
  items: any[];
  joinedGroups: any[];
}

const CACHE_TTL_MS = 5 * 60 * 1000;

const clone = <T>(value: T): T => {
  return JSON.parse(JSON.stringify(value));
};

const createEntry = <T>(): CachedEntry<T> => ({
  data: null,
  lastFetchedAt: 0,
  loading: false,
  error: null,
  inFlightPromise: null,
  storeId: "",
});

export const useStockDataCacheStore = defineStore("stockDataCache", () => {
  const entries = reactive<Record<StockCacheKey, CachedEntry<any>>>({
    stockList: createEntry<any[]>(),
    productMenu: createEntry<ProductMenuData>(),
    toppingMenu: createEntry<ToppingMenuData>(),
    stockFormula: createEntry<any[]>(),
    toppingFormula: createEntry<any[]>(),
    stockTransactions: createEntry<any[]>(),
    stockUserPins: createEntry<any[]>(),
  });

  const getStoreId = () => useAuthStore().storeId || "";

  const isFresh = (entry: CachedEntry<any>, storeId: string) => {
    return (
      entry.data !== null &&
      entry.storeId === storeId &&
      Date.now() - entry.lastFetchedAt < CACHE_TTL_MS
    );
  };

  const fetchCached = async <T>(
    key: StockCacheKey,
    loader: () => Promise<T>,
    options: CachedFetchOptions = {},
  ): Promise<T> => {
    const storeId = getStoreId();
    const entry = entries[key] as CachedEntry<T>;

    if (!options.force && isFresh(entry, storeId)) {
      return clone(entry.data as T);
    }

    if (!options.force && entry.inFlightPromise && entry.storeId === storeId) {
      return clone(await entry.inFlightPromise);
    }

    entry.loading = true;
    entry.error = null;
    entry.storeId = storeId;

    entry.inFlightPromise = loader()
      .then((data) => {
        entry.data = clone(data);
        entry.lastFetchedAt = Date.now();
        return data;
      })
      .catch((error) => {
        entry.error = error;
        throw error;
      })
      .finally(() => {
        entry.loading = false;
        entry.inFlightPromise = null;
      });

    return clone(await entry.inFlightPromise);
  };

  const invalidate = (keys?: StockCacheKey[]) => {
    const keysToClear = keys || (Object.keys(entries) as StockCacheKey[]);

    keysToClear.forEach((key) => {
      const entry = entries[key];
      entry.data = null;
      entry.lastFetchedAt = 0;
      entry.loading = false;
      entry.error = null;
      entry.inFlightPromise = null;
      entry.storeId = "";
    });
  };

  const clearAll = () => invalidate();

  const getStockList = (options?: CachedFetchOptions) => {
    const dataservice = useDataservice();
    return fetchCached("stockList", async () => {
      const data = await dataservice.stock.list.fetch({
        customerId: getStoreId(),
      });
      // Drop ghost/empty items: a deleted stock can still get its balance topped
      // up by the legacy backend, leaving a nameless placeholder. Filter anything
      // without a real (non-empty string) name so it never reaches the UI.
      return ((data || []) as any[]).filter(
        (item) => typeof item?.name === "string" && item.name.trim() !== "",
      );
    }, options);
  };

  // Live stock only. Deletion in the Stock tab is a SOFT delete (`deleted: true`)
  // so the record survives for name resolution in Formula / History. Every screen
  // that lets the user pick, adjust or report on stock must use this accessor —
  // `getStockList` returns soft-deleted rows too and is only for the
  // name-resolution paths (Formula map, History log).
  const getActiveStockList = async (options?: CachedFetchOptions) => {
    const list = (await getStockList(options)) as any[];
    return (list || []).filter((item: any) => item?.deleted !== true);
  };

  const getProductMenu = (options?: CachedFetchOptions) => {
    const dataservice = useDataservice();
    return fetchCached("productMenu", async () => {
      await Promise.all([
        dataservice.catalogs.fetch(getStoreId()),
        dataservice.items.fetch(getStoreId()),
        dataservice.subItems.fetch(getStoreId()),
      ]);

      const catalogs = APIMenuMapper.mapData(
        dataservice.catalogs.data.value as any[][],
        "catalogs",
      ) as CatalogItem[];
      const items = APIMenuMapper.mapData(
        dataservice.items.data.value as any[][],
        "items",
      ) as ItemData[];
      const subItems = APIMenuMapper.mapData(
        dataservice.subItems.data.value as any[][],
        "subitems",
      ) as SubItemData[];

      return {
        catalogs,
        items,
        subItems,
        productCatalogs: APIMenuMapper.catalogsJoined(
          catalogs,
          items,
          subItems,
        ),
      };
    }, options);
  };

  const getToppingMenu = (options?: CachedFetchOptions) => {
    const dataservice = useDataservice();
    return fetchCached("toppingMenu", async () => {
      const [groups, items] = await Promise.all([
        dataservice.toppingGroups.fetch(),
        dataservice.toppingItems.fetch(),
      ]);

      const groupMap = new Map();
      (items || []).forEach((item: any) => {
        const groupId = item.GroupId || "ungrouped";
        if (!groupMap.has(groupId)) {
          const group = (groups || []).find((g: any) => g.Id === groupId);
          groupMap.set(groupId, {
            Id: groupId,
            Name: group?.Name || "Ungrouped",
            Toppings: [],
          });
        }
        groupMap.get(groupId).Toppings.push({
          Id: item.Id,
          Name: item.Name,
          Row: item.Row,
          Price: item.Price,
          GroupId: item.GroupId,
        });
      });

      return {
        groups: groups || [],
        items: items || [],
        joinedGroups: Array.from(groupMap.values()),
      };
    }, options);
  };

  const getStockFormula = (options?: CachedFetchOptions) => {
    const dataservice = useDataservice();
    return fetchCached("stockFormula", async () => {
      const data = await dataservice.stockFormula.fetch({
        partitionKey: getStoreId(),
      });
      return (data || []) as any[];
    }, options);
  };

  const getToppingFormula = (options?: CachedFetchOptions) => {
    const dataservice = useDataservice();
    return fetchCached("toppingFormula", async () => {
      const data = await dataservice.toppingFormula.fetch({
        partitionKey: getStoreId(),
      });
      return (data || []) as any[];
    }, options);
  };

  const getStockTransactions = (options?: CachedFetchOptions) => {
    const dataservice = useDataservice();
    return fetchCached("stockTransactions", async () => {
      const data = await dataservice.stock.transactions.fetch({
        customerId: getStoreId(),
      });
      return (data || []) as any[];
    }, options);
  };

  // Raw network fetch for the stock-user-PIN list, normalized to a flat array.
  // Shared by the cache loader below and the versionSync registration so both
  // paths return the identical shape stored in the `stockUserPin` IDB mirror.
  // NB: the resource/mirror key is `stockUserPin` (NOT `employeePin`) to avoid
  // colliding with the POS cashier "employee PIN" concept — different feature.
  const fetchStockUserPinList = async (): Promise<any[]> => {
    const dataservice = useDataservice();
    const raw = await dataservice.stockEmployeesPin.fetch({
      customerId: getStoreId(),
    });
    return Array.isArray(raw)
      ? raw
      : Array.isArray(raw?.data)
        ? raw.data
        : Array.isArray(raw?.items)
          ? raw.items
          : [];
  };

  // Push a freshly-reconciled list into the in-memory cache entry so any
  // mounted stock screen sees it without another fetch. Called by versionSync
  // (boot reconcile / post-write refresh) via the registration below.
  const applyStockUserPins = (list: any[]) => {
    const entry = entries.stockUserPins;
    entry.data = clone(Array.isArray(list) ? list : []);
    entry.lastFetchedAt = Date.now();
    entry.storeId = getStoreId();
    entry.error = null;
  };

  // Register `stockUserPin` as a version-tracked resource so every device
  // reconciles it against the Firestore version doc at boot: when one terminal
  // edits a stock PIN and bumps the version (see Tab4StockPin), the others
  // refetch on their next bootstrap instead of serving the stale IDB mirror.
  // Idempotent (registry is a Map) — safe to call from boot and again lazily.
  const init = () => {
    registerResource<any[]>({
      resource: "stockUserPin",
      refetch: fetchStockUserPinList,
      applyToStore: applyStockUserPins,
    });
  };

  const getStockUserPins = (options?: CachedFetchOptions) => {
    return fetchCached("stockUserPins", async () => {
      // Cache-first via IDB so the stock screens still work offline; fall
      // through to network on miss. On force, skip the mirror and hit the
      // network so writes made elsewhere (e.g. the settings PIN editor) are
      // reflected — otherwise the stale IDB mirror is served until the cache
      // is cleared by hand.
      if (!options?.force) {
        const cached = await readMirror<any[]>('stockUserPin');
        if (cached != null) return cached;
      }

      try {
        const list = await fetchStockUserPinList();
        await writeMirror('stockUserPin', list);
        return list;
      } catch (err) {
        console.warn('[stockUserPin] fetch failed', err);
        return [];
      }
    }, options);
  };

  return {
    entries,
    init,
    invalidate,
    clearAll,
    getStockList,
    getActiveStockList,
    getProductMenu,
    getToppingMenu,
    getStockFormula,
    getToppingFormula,
    getStockTransactions,
    getStockUserPins,
    fetchStockUserPinList,
    applyStockUserPins,
  };
});
